import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Select,
  Option,
  Sheet,
  Table,
  Typography,
  IconButton,
  Button,
  Stack,
  Tabs,
  Tab,
  TabList,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  useTheme
} from '@mui/joy';
import { IconWrapper } from './IconWrapper';
import { FaEye, FaEnvelope, FaFilter, FaSort, FaWindowMaximize, FaThLarge } from 'react-icons/fa';
import { Order, OrderStatus } from '../types/order';
import { sendOrderEmail } from '../utils/graphService';
import { useOrderStore } from '../hooks/useOrder';
import type { Database } from '../types/supabase';

type Supplier = Database['public']['Tables']['suppliers']['Row'];

// Extend the Order type to include order type
interface ExtendedOrder extends Order {
  orderType: 'glass' | 'window';
}

const OrderHistory: React.FC = () => {
  const { orders: storeOrders, updateOrderStatus } = useOrderStore();
  const [orders, setOrders] = useState<ExtendedOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<ExtendedOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<ExtendedOrder | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'total'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [emailStatus, setEmailStatus] = useState({ sending: false, error: null as string | null });
  const [orderTypeTab, setOrderTypeTab] = useState<'all' | 'glass' | 'window'>('all');

  useEffect(() => {
    // Convert orders from store to extended orders with type
    // For this example, we'll determine type based on a property or id pattern
    // In a real application, this would come from your database
    const extendedOrders: ExtendedOrder[] = storeOrders.map(order => ({
      ...order,
      // Sample logic to identify order type - this is just an example
      // You would need to adapt this to your actual data structure
      orderType: order.id.includes('window') || order.items.some(item => 'windowType' in item) 
        ? 'window' 
        : 'glass'
    }));
    
    setOrders(extendedOrders);
    setFilteredOrders(extendedOrders);
  }, [storeOrders]);

  useEffect(() => {
    // Filter and sort orders whenever filter/sort criteria change
    let result = [...orders];

    // Apply order type filter
    if (orderTypeTab !== 'all') {
      result = result.filter(order => order.orderType === orderTypeTab);
    }

    // Apply search filter
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      result = result.filter(order => 
        order.orderNumber.toLowerCase().includes(lowerSearchTerm) ||
        order.customerInfo.name.toLowerCase().includes(lowerSearchTerm) ||
        order.customerInfo.email.toLowerCase().includes(lowerSearchTerm) ||
        (order.customerInfo.company && order.customerInfo.company.toLowerCase().includes(lowerSearchTerm))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(order => order.status === statusFilter);
    }

    // Apply sorting
    result.sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      } else {
        return sortDirection === 'asc' ? a.total - b.total : b.total - a.total;
      }
    });

    setFilteredOrders(result);
  }, [orders, searchTerm, statusFilter, sortBy, sortDirection, orderTypeTab]);

  const handleViewOrder = (order: ExtendedOrder) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleSendEmail = async (order: ExtendedOrder) => {
    setSelectedOrder(order);
    setIsEmailModalOpen(true);
  };

  const handleEmailSend = async () => {
    if (!selectedOrder) return;
    
    setEmailStatus({ sending: true, error: null });
    
    try {
      // Attempt to send email using Microsoft Graph
      await sendOrderEmail(selectedOrder, selectedOrder.supplier?.email || 'info@keystoneglazing.co.uk', selectedOrder.orderNumber.startsWith('WND-'));
      
      // Update order status using the store function instead of directly updating localStorage
      updateOrderStatus(selectedOrder.id, 'processing');
      
      // Update local state to reflect changes
      const updatedOrders = orders.map(o => 
        o.id === selectedOrder.id ? { ...o, status: 'processing' as OrderStatus } : o
      );
      setOrders(updatedOrders);
      
      setEmailStatus({ sending: false, error: null });
      setIsEmailModalOpen(false);
    } catch (error) {
      setEmailStatus({ 
        sending: false, 
        error: error instanceof Error ? error.message : 'Failed to send email' 
      });
    }
  };

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'pending': return 'neutral';
      case 'processing': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'danger';
      default: return 'neutral';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography level="h2" mb={2}>Order History</Typography>

      {/* Order Type Tabs */}
      <Tabs 
        value={orderTypeTab} 
        onChange={(_, value) => setOrderTypeTab(value as 'all' | 'glass' | 'window')}
        sx={{ mb: 3 }}
      >
        <TabList>
          <Tab value="all" color={orderTypeTab === 'all' ? 'primary' : 'neutral'} variant={orderTypeTab === 'all' ? 'soft' : 'plain'}>All Orders</Tab>
          <Tab value="glass" color={orderTypeTab === 'glass' ? 'primary' : 'neutral'} variant={orderTypeTab === 'glass' ? 'soft' : 'plain'}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton>
                <IconWrapper icon={FaThLarge} />
              </IconButton>
              <span>Glass Orders</span>
            </Box>
          </Tab>
          <Tab value="window" color={orderTypeTab === 'window' ? 'primary' : 'neutral'} variant={orderTypeTab === 'window' ? 'soft' : 'plain'}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton>
                <IconWrapper icon={FaWindowMaximize} />
              </IconButton>
              <span>Window Orders</span>
            </Box>
          </Tab>
        </TabList>
      </Tabs>

      {/* Filters and search */}
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <Input
            placeholder="Search orders..."
            startDecorator={<IconButton>
              <IconWrapper icon={FaFilter} />
            </IconButton>}
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          />
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <Select
            placeholder="Status"
            value={statusFilter}
            onChange={(_, value) => setStatusFilter(value as OrderStatus | 'all')}
          >
            <Option value="all">All Statuses</Option>
            <Option value="pending">Pending</Option>
            <Option value="processing">Processing</Option>
            <Option value="completed">Completed</Option>
            <Option value="cancelled">Cancelled</Option>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <Select
            placeholder="Sort by"
            value={sortBy}
            onChange={(_, value) => setSortBy(value as 'date' | 'total')}
            endDecorator={
              <IconButton 
                variant="plain" 
                color="neutral" 
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  toggleSortDirection();
                }}
              >
                <IconButton>
                  <IconWrapper icon={FaSort} />
                </IconButton>
                {sortDirection === 'asc' ? '↑' : '↓'}
              </IconButton>
            }
          >
            <Option value="date">Date</Option>
            <Option value="total">Total</Option>
          </Select>
        </FormControl>
      </Box>

      {/* Orders table */}
      <Card>
        <Sheet sx={{ height: '500px', overflow: 'auto' }}>
          <Table stickyHeader>
            <thead>
              <tr>
                <th style={{ width: '15%' }}>Order No.</th>
                <th style={{ width: '15%' }}>Date</th>
                <th style={{ width: '25%' }}>Customer</th>
                <th style={{ width: '10%' }}>Items</th>
                <th style={{ width: '15%' }}>Total</th>
                <th style={{ width: '10%' }}>Status</th>
                <th style={{ width: '10%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center' }}>
                    <Typography level="body-sm">No orders found.</Typography>
                  </td>
                </tr>
              ) : (
                filteredOrders.map(order => (
                  <tr key={order.id}>
                    <td>{order.orderNumber}</td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>
                      <Typography level="body-sm">{order.customerInfo.name}</Typography>
                      {order.customerInfo.company && (
                        <Typography level="body-xs" color="neutral">
                          {order.customerInfo.company}
                        </Typography>
                      )}
                    </td>
                    <td>{order.items.length}</td>
                    <td>{formatCurrency(order.total)}</td>
                    <td>
                      <Chip 
                        size="sm" 
                        color={getStatusColor(order.status)}
                      >
                        {order.status}
                      </Chip>
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton 
                          size="sm" 
                          variant="plain"
                          onClick={() => handleViewOrder(order)}
                        >
                          <IconButton>
                            <IconWrapper icon={FaEye} />
                          </IconButton>
                        </IconButton>
                        {order.status === 'pending' && (
                          <IconButton 
                            size="sm" 
                            variant="plain"
                            onClick={() => handleSendEmail(order)}
                          >
                            <IconButton>
                              <IconWrapper icon={FaEnvelope} />
                            </IconButton>
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Sheet>
      </Card>

      {/* Order details modal */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalDialog 
          aria-labelledby="order-details-modal"
          size="lg"
          sx={{ maxWidth: 700 }}
        >
          <ModalClose onClick={() => setIsModalOpen(false)} />
          {selectedOrder && (
            <>
              <Typography level="h4">
                Order #{selectedOrder.orderNumber}
              </Typography>
              <Typography level="body-md" color="neutral">
                {formatDate(selectedOrder.createdAt)}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 3 }}>
                <Typography level="title-md">Customer Information</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
                  <Box>
                    <Typography level="body-xs" color="neutral">Name</Typography>
                    <Typography>{selectedOrder.customerInfo.name}</Typography>
                  </Box>
                  <Box>
                    <Typography level="body-xs" color="neutral">Email</Typography>
                    <Typography>{selectedOrder.customerInfo.email}</Typography>
                  </Box>
                  <Box>
                    <Typography level="body-xs" color="neutral">Phone</Typography>
                    <Typography>{selectedOrder.customerInfo.phone}</Typography>
                  </Box>
                  <Box>
                    <Typography level="body-xs" color="neutral">Company</Typography>
                    <Typography>{selectedOrder.customerInfo.company || '—'}</Typography>
                  </Box>
                  {selectedOrder.customerInfo.address && (
                    <Box sx={{ gridColumn: 'span 2' }}>
                      <Typography level="body-xs" color="neutral">Address</Typography>
                      <Typography>{selectedOrder.customerInfo.address}</Typography>
                    </Box>
                  )}
                  {selectedOrder.customerInfo.notes && (
                    <Box sx={{ gridColumn: 'span 2' }}>
                      <Typography level="body-xs" color="neutral">Notes</Typography>
                      <Typography>{selectedOrder.customerInfo.notes}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              
              <Typography level="title-md" mb={1}>Order Items</Typography>
              <Sheet sx={{ mb: 3, maxHeight: '200px', overflow: 'auto' }}>
                <Table>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Details</th>
                      <th style={{ textAlign: 'right' }}>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.type} {item.thickness}</td>
                        <td>
                          <Typography level="body-xs">
                            {item.width}mm × {item.height}mm
                            {item.quantity > 1 ? ` × ${item.quantity}` : ''}
                          </Typography>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {formatCurrency(item.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography level="body-md">Subtotal</Typography>
                <Typography level="body-md">{formatCurrency(selectedOrder.subtotal)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography level="body-md">VAT</Typography>
                <Typography level="body-md">{formatCurrency(selectedOrder.vat)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography level="title-md">Total</Typography>
                <Typography level="title-md">{formatCurrency(selectedOrder.total)}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip color={getStatusColor(selectedOrder.status)}>
                  {selectedOrder.status}
                </Chip>
                
                {selectedOrder.status === 'pending' && (
                  <Button 
                    onClick={() => {
                      setIsModalOpen(false);
                      handleSendEmail(selectedOrder);
                    }}
                    startDecorator={<IconButton>
                      <IconWrapper icon={FaEnvelope} />
                    </IconButton>}
                  >
                    Send to Supplier
                  </Button>
                )}
              </Box>
            </>
          )}
        </ModalDialog>
      </Modal>

      {/* Email order modal */}
      <Modal open={isEmailModalOpen} onClose={() => setIsEmailModalOpen(false)}>
        <ModalDialog 
          aria-labelledby="email-order-modal"
          sx={{ maxWidth: 500 }}
        >
          <ModalClose onClick={() => setIsEmailModalOpen(false)} />
          {selectedOrder && (
            <>
              <Typography level="h4">
                Send Order to Supplier
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography mb={2}>
                Send order #{selectedOrder.orderNumber} to supplier via email?
              </Typography>
              
              {selectedOrder.supplier ? (
                <Box sx={{ mb: 3 }}>
                  <Typography level="body-xs" color="neutral">Supplier</Typography>
                  <Typography level="body-md">{selectedOrder.supplier.supplier_name}</Typography>
                  <Typography level="body-xs" color="neutral">Email</Typography>
                  <Typography level="body-md">{selectedOrder.supplier.email}</Typography>
                </Box>
              ) : (
                <Typography level="body-md" color="danger" mb={3}>
                  No supplier assigned to this order.
                </Typography>
              )}
              
              {emailStatus.error && (
                <Typography color="danger" mb={2}>
                  Error: {emailStatus.error}
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  color="neutral" 
                  onClick={() => setIsEmailModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleEmailSend}
                  loading={emailStatus.sending}
                  disabled={!selectedOrder.supplier || emailStatus.sending}
                >
                  Send Email
                </Button>
              </Box>
            </>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default OrderHistory; 